import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LinkIcon from '@material-ui/icons/Link';
import SEO from './components/SEO';
import { ThemeContext } from './components/Core/ThemeContext';
import { getPerspectiveBySlug, getPerspectiveSource } from './data/perspectives';
import { getPerspectiveReviewStage, PERSPECTIVE_REVIEW_STAGES } from './utils/perspectiveReviewStages';
import './perspectives.css';

const getSlugFromPath = () => decodeURIComponent(window.location.pathname.split('/').filter(Boolean)[1] || '');
const getReadingFromHash = (topic) => {
    const readingId = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    return topic?.readings.find((reading) => reading.id === readingId);
};

function AssociationBadges({ badges }) {
    return (
        <div className="perspective-badges" aria-label="Common associations">
            {badges.map((badge) => <span key={badge}>{badge}</span>)}
        </div>
    );
}

function getRelationshipDestination(topic, relationship) {
    if (relationship.topicSlug) {
        const targetTopic = getPerspectiveBySlug(relationship.topicSlug);
        const targetReading = relationship.readingId
            ? targetTopic?.readings.find((reading) => reading.id === relationship.readingId)
            : null;
        return {
            label: relationship.label || targetReading?.title || targetTopic?.question,
            href: targetTopic
                ? `/perspectives/${targetTopic.slug}${targetReading ? `#${targetReading.id}` : ''}`
                : null,
        };
    }

    const targetReading = relationship.target
        ? topic.readings.find((reading) => reading.id === relationship.target)
        : null;
    return {
        label: relationship.label || targetReading?.title,
        href: targetReading ? `#${targetReading.id}` : null,
    };
}

function RelationshipLink({ className, href, children }) {
    if (href?.startsWith('/')) return <Link className={className} to={href}>{children}</Link>;
    return <a className={className} href={href}>{children}</a>;
}

function RelationList({ topic, relations }) {
    return (
        <div className="perspective-relations">
            <h4>Related ideas</h4>
            <ul>
                {relations.map((relation, index) => {
                    const destination = getRelationshipDestination(topic, relation);
                    const isLinked = relation.linked && destination.href;
                    return (
                        <li key={`${relation.type}-${destination.label}-${index}`}>
                            {isLinked ? (
                                <RelationshipLink className="perspective-relation is-linked" href={destination.href}>
                                    <LinkIcon aria-hidden="true" />
                                    <span className="perspective-relation-type">{relation.type}</span>
                                    <strong>{destination.label}</strong>
                                </RelationshipLink>
                            ) : (
                                <span className="perspective-relation is-unlinked">
                                    <LinkIcon aria-hidden="true" />
                                    <span className="perspective-relation-type">{relation.type}</span>
                                    <strong>{destination.label}</strong>
                                    <small>Not linked yet</small>
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

function PredicateNode({ topic, predicate, isRoot = false }) {
    if (isRoot) {
        return (
            <li className="perspective-predicate-tree-root">
                <div className="perspective-predicate-argument">
                    <strong>{predicate.label}</strong>
                    {predicate.thesis ? <span>{predicate.thesis}</span> : null}
                </div>
                {predicate.children?.length ? (
                    <ul className="perspective-predicate-tree-children">
                        {predicate.children.map((child) => <PredicateNode key={`${child.canonicalId}-${child.readingId}`} topic={topic} predicate={child} />)}
                    </ul>
                ) : null}
            </li>
        );
    }

    const destination = getRelationshipDestination(topic, predicate);
    const isLinked = predicate.linked && destination.href;
    const contents = (
        <>
            <span className="perspective-predicate-marker" aria-hidden="true">→</span>
            <span className="perspective-predicate-copy">
                <strong>{predicate.argument || predicate.label || destination.label}</strong>
                <small className="perspective-predicate-foundation">{destination.label}</small>
            </span>
            {!isLinked ? <small>Not linked yet</small> : null}
        </>
    );

    return (
        <li>
            {isLinked ? (
                <RelationshipLink className="perspective-predicate is-linked" href={destination.href}>{contents}</RelationshipLink>
            ) : (
                <span className="perspective-predicate is-unlinked">{contents}</span>
            )}
        </li>
    );
}

function PredicateList({ topic, predicates = [], predicateTree = [] }) {
    return (
        <div className="perspective-predicates">
            <div className="perspective-dependency-heading">
                <h4>Predicated on</h4>
                <span>Interpretive commitments this view depends on</span>
            </div>
            <ul className="perspective-predicate-tree">
                {(predicateTree.length ? predicateTree : [{ label: 'This view', children: predicates }]).map((predicate) => (
                    <PredicateNode key={predicate.id || predicate.label} topic={topic} predicate={predicate} isRoot />
                ))}
            </ul>
        </div>
    );
}

function ReadingCard({ topic, reading, open, onToggle }) {
    return (
        <article id={reading.id} className={`perspective-reading perspective-reading-${reading.accent} ${open ? 'is-open' : ''}`}>
            <span className="perspective-reading-strip" aria-hidden="true" />
            <div className="perspective-reading-main">
                <button
                    type="button"
                    className="perspective-reading-toggle"
                    onClick={onToggle}
                    aria-expanded={open}
                    aria-controls={`${reading.id}-content`}
                >
                    <span className="perspective-reading-heading">
                        <span className="perspective-reading-title-row">
                            <h2>{reading.title}</h2>
                            <AssociationBadges badges={reading.badges} />
                        </span>
                        <span className="perspective-reading-thesis">{reading.thesis}</span>
                    </span>
                    <ExpandMoreIcon className="perspective-reading-chevron" aria-hidden="true" />
                </button>

                {open ? (
                    <div id={`${reading.id}-content`} className="perspective-reading-content">
                        <div className="perspective-reading-copy">
                            <section>
                                <h3>Summary</h3>
                                <p>{reading.summary}</p>
                            </section>
                            <section>
                                <h3>What this contributes</h3>
                                <p>{reading.contribution}</p>
                            </section>
                            <section className="perspective-caution">
                                <h3>Question to keep in view</h3>
                                <p>{reading.caution}</p>
                            </section>
                        </div>

                        <aside className="perspective-voices">
                            <h3>Associated voices</h3>
                            <ul>
                                {reading.supporters.map((supporter) => {
                                    const source = getPerspectiveSource(topic, supporter.sourceId);
                                    return (
                                        <li key={supporter.name}>
                                            <a href={source.href} target="_blank" rel="noopener noreferrer">
                                                {supporter.name}
                                            </a>
                                            <span>{source.title}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </aside>

                        <PredicateList topic={topic} predicates={reading.predicates} predicateTree={reading.predicateTree} />
                        <RelationList topic={topic} relations={reading.relations} />
                    </div>
                ) : null}
            </div>
        </article>
    );
}

function SourceSection({ topic }) {
    const reviewStage = getPerspectiveReviewStage(topic.generation.status);

    return (
        <details className="perspective-sources">
            <summary>
                <span>
                    <span className="perspectives-eyebrow">Transparency</span>
                    <strong>Sources, generation and review</strong>
                </span>
                <span className={`perspective-review-status perspective-review-stage-${reviewStage.id}`} title={reviewStage.explanation}>{reviewStage.status}</span>
            </summary>
            <div className="perspective-sources-body">
                <div className="perspective-generation-record">
                    <h2>AI generation record</h2>
                    <dl>
                        <div><dt>System</dt><dd>{topic.generation.system}</dd></div>
                        <div><dt>Generated</dt><dd>{topic.generation.date}</dd></div>
                        <div><dt>Review</dt><dd>{reviewStage.status}</dd></div>
                    </dl>
                    <p>{topic.generation.note}</p>
                </div>
                <ol className="perspective-source-list">
                    {topic.sources.map((source) => (
                        <li key={source.id} id={`source-${source.id}`}>
                            <a href={source.href} target="_blank" rel="noopener noreferrer">
                                {source.author}, <cite>{source.title}</cite>
                            </a>
                            <p>{source.detail}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </details>
    );
}

function PagePerspective() {
    const theme = useContext(ThemeContext);
    const topic = getPerspectiveBySlug(getSlugFromPath());
    const reviewStage = getPerspectiveReviewStage(topic?.generation.status);
    const [openReadings, setOpenReadings] = useState(() => {
        const initialReading = getReadingFromHash(topic);
        return new Set(initialReading ? [initialReading.id] : []);
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        theme.loading(false);
    }, [theme]);

    useEffect(() => {
        const openHashReading = () => {
            if (!topic) return;
            const hashReading = getReadingFromHash(topic);
            setOpenReadings(new Set(hashReading ? [hashReading.id] : []));

            if (hashReading) {
                window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                        document.getElementById(hashReading.id)?.scrollIntoView({ block: 'start' });
                    });
                });
            }
        };

        openHashReading();
        window.addEventListener('hashchange', openHashReading);
        return () => window.removeEventListener('hashchange', openHashReading);
    }, [topic?.slug]);

    // The hidden /perspectives/chart route also matches this dynamic route in
    // the app's non-exclusive route list. Let the dedicated chart page render
    // without overlaying a misleading "Perspective not found" page.
    if (!topic && getSlugFromPath() === 'chart') return null;

    if (!topic) {
        return (
            <main className="perspectives-page">
                <SEO canonical="/perspectives" title="Perspective Not Found | ai-BIBLE" description="The requested perspective could not be found." noindex />
                <div className="perspectives-frame perspective-not-found">
                    <h1>Perspective not found</h1>
                    <Link to="/perspectives">Return to Perspectives</Link>
                </div>
            </main>
        );
    }

    const toggleReading = (readingId) => {
        setOpenReadings((current) => {
            const next = new Set(current);
            if (next.has(readingId)) next.delete(readingId);
            else next.add(readingId);
            return next;
        });
    };

    const canonical = `/perspectives/${topic.slug}`;
    const title = `${topic.shortTitle} | Bible Perspectives`;
    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: topic.question,
        description: topic.seoDescription,
        url: `https://www.ai-bible.com${canonical}`,
        dateModified: topic.lastModified,
        isPartOf: {
            '@type': 'CollectionPage',
            name: 'Bible Perspectives',
            url: 'https://www.ai-bible.com/perspectives',
        },
        about: topic.readings.map((reading) => reading.title),
    };

    return (
        <main className="perspectives-page perspective-detail">
            <SEO canonical={canonical} title={title} description={topic.seoDescription} type="article" structuredData={structuredData} />
            <div className="perspectives-frame">
                <nav className="perspective-breadcrumb" aria-label="Breadcrumb">
                    <Link to="/perspectives">Perspectives</Link>
                    <span aria-hidden="true">/</span>
                    <span>{topic.category}</span>
                </nav>

                <header className="perspectives-question">
                    <span className="perspectives-question-strip" aria-hidden="true" />
                    <div className="perspectives-question-copy">
                        <p className="perspectives-eyebrow">{topic.category}</p>
                        <h1>{topic.question}</h1>
                        <p>{topic.summary}</p>
                    </div>
                </header>

                <details className={`perspectives-ai-note perspective-ai-warning perspective-review-disclosure perspective-review-stage-${reviewStage.id}`}>
                    <summary className="perspective-review-disclosure-summary">
                        <span className="perspectives-ai-icon" aria-hidden="true">✦</span>
                        <span className="perspective-review-disclosure-copy">
                            <strong>AI-generated · {reviewStage.status}.</strong>
                            <span> {reviewStage.explanation} Use this page to compare cited readings, not as a substitute for the biblical text or the sources below.</span>
                        </span>
                        <span className="perspective-review-disclosure-arrow" aria-hidden="true">⌄</span>
                    </summary>
                    <div className="perspective-review-expanded">
                        <p>How review stages progress</p>
                        <ol>
                            {PERSPECTIVE_REVIEW_STAGES.map((stage) => (
                                <li key={stage.id}>
                                    <span className={`perspective-review-status perspective-review-stage-${stage.id}`}>
                                        {stage.id === 'graduate-citations-checked' ? 'Graduate Reviewed' : stage.id === 'expert-reviewed' ? 'Expert Reviewed' : stage.status}
                                    </span>
                                    <span>{stage.explanation}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </details>

                <section className="perspective-orientation" aria-labelledby="perspective-orientation-title">
                    <div>
                        <p className="perspectives-eyebrow">The question</p>
                        <h2 id="perspective-orientation-title">Where the disagreement lies</h2>
                        <p>{topic.introduction}</p>
                    </div>
                    <div className="perspective-key-texts">
                        <h3>Key texts</h3>
                        <div>
                            {topic.keyTexts.map((text) => <a href={text.href} key={text.href}>{text.label}</a>)}
                        </div>
                    </div>
                </section>

                <h2 className="perspective-readings-title" id="perspective-readings-title">Different Perspectives</h2>

                <section className="perspective-readings" aria-labelledby="perspective-readings-title">
                    {topic.readings.map((reading) => (
                        <ReadingCard
                            topic={topic}
                            reading={reading}
                            open={openReadings.has(reading.id)}
                            onToggle={() => toggleReading(reading.id)}
                            key={reading.id}
                        />
                    ))}
                </section>

                <SourceSection topic={topic} />
            </div>
        </main>
    );
}

export default PagePerspective;
